<template>
  <div class="d-flex flex-column row-gap-3" data-locator="access-rights">
    <div class="text--medium text--extra-bold text-text-primary">
      {{ $t('project.project-settings.access-rights.AccessRights.title') }}
    </div>

    <app-alert-in-page
      v-if="isDataRoom"
      :dismissible="false"
      :has-left-border="false"
      variant="info"
      data-locator="watermark-info-banner"
    >
      <span class="text--small text-text-secondary">
        {{ $t('project.project-settings.access-rights.AccessRights.watermarkInfoBanner') }}
      </span>
    </app-alert-in-page>

    <AppCard elevation="0">
      <template v-if="roomRightsPending && !roomRights">
        <div class="text-center">
          <AppLoader class="mx-auto"></AppLoader>
        </div>
      </template>
      <template v-if="roomRights">
        <v-progress-linear v-if="patchRightsPending"
                           indeterminate
                           color="surface-info-bold"
                           bg-color="surface-grey"
        />
        <div class="AccessRightsTable-grid">
          <AccessRightsHeader :grid-styles="gridStyles"
                              :displayed-columns="displayedColumns"
          />
          <AccessRightsGlobal :grid-styles="gridStyles"
                              :patch-rights-pending="patchRightsPending"
                              :displayed-columns="displayedColumns"
                              @patchRoomRightsSuccess="patchRoomRightsSuccess"
                              @patchRoomRightsError="patchRoomRightsError"
          />

          <template v-for="group in roomRights.groups" :key="`group-${group.id}-name`">
            <AccessRightsGroup :patch-rights-pending="patchRightsPending"
                               :grid-styles="gridStyles"
                               :group="group"
                               :is-open="openedGroups.includes(group.id)"
                               :displayed-columns="displayedColumns"
                               @toggleGroup="toggleGroup"
                               @patchRoomRightsSuccess="patchRoomRightsSuccess"
                               @patchRoomRightsError="patchRoomRightsError"
            />
          </template>
        </div>
      </template>
    </AppCard>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppLoader from '@/common/AppLoader.vue'
import { toast } from '@/common/utils/toast'
import { ROOMS_ROUTE_NAME } from '@/router/routerData'
import { GET_ROOM_RIGHTS } from '@/store/modules/room/action_types'
import { RESET_ROOM_RIGHTS } from '@/store/modules/room/mutation_types'

import AccessRightsGlobal from './AccessRightsGlobal.vue'
import AccessRightsGroup from './AccessRightsGroup.vue'
import AccessRightsHeader from './AccessRightsHeader.vue'
import { RIGHTS_LIST } from './rights-list'

export default {
  name: 'AccessRights',
  components: {
    AccessRightsGlobal,
    AccessRightsGroup,
    AccessRightsHeader,
    AppCard,
    AppLoader,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      openedGroups: [],
    }
  },
  computed: {
    ...mapState('room',
      [
        'roomRights',
        'roomRightsPending',
        'patchRoomGlobalRightsPending',
        'patchRoomGroupRightsPending',
        'patchRoomUserRightsPending',
      ]),
    ...mapGetters('room', ['isDataRoom']),
    patchRightsPending () {
      return this.patchRoomGlobalRightsPending ||
        this.patchRoomGroupRightsPending ||
        this.patchRoomUserRightsPending
    },
    gridStyles () {
      let columnWidth = [150, 100]
      if (this.$vuetify.display.smAndUp) {
        columnWidth = [250, 150]
      }
      if (this.$vuetify.display.lgAndUp) {
        columnWidth = [350, 200]
      }
      return {
        display: 'grid',
        gridTemplateColumns: `${columnWidth[0]}px repeat(${this.templateVariables['--access-rights-columns-count']}, ${columnWidth[1]}px)`,
      }
    },
    displayedColumns () {
      const bibleCreationRightColumn = {
        ...RIGHTS_LIST.CAN_CREATE_BIBLES,
        hasHeaderTooltip: this.isDataRoom,
        disableCheck: {
          global: () => {
            const dontHaveDownloadRight = this.isDataRoom && !this.roomRights.global[RIGHTS_LIST.CAN_DOWNLOAD.propertyName]
            return this.patchRightsPending || dontHaveDownloadRight
          },
          group: (group) => {
            const dontHaveDownloadRight = this.isDataRoom && !group[RIGHTS_LIST.CAN_DOWNLOAD.propertyName]
            return this.patchRightsPending || Boolean(group.isPm) || dontHaveDownloadRight
          },
          member: (member, isGroupPm) => {
            const dontHaveDownloadRight = this.isDataRoom && !member[RIGHTS_LIST.CAN_DOWNLOAD.propertyName]
            return this.patchRightsPending || isGroupPm || dontHaveDownloadRight
          },
        },
      }

      if (this.isDataRoom) {
        return [
          RIGHTS_LIST.DISPLAY_WATERMARK,
          RIGHTS_LIST.CAN_DOWNLOAD,
          RIGHTS_LIST.CAN_UPLOAD,
          RIGHTS_LIST.CAN_CREATE_FOLDERS,
          bibleCreationRightColumn,
          RIGHTS_LIST.CAN_ADD_QUESTIONS,
        ]
      }

      return [
        RIGHTS_LIST.CAN_UPLOAD,
        RIGHTS_LIST.CAN_CREATE_FOLDERS,
        bibleCreationRightColumn,
        RIGHTS_LIST.CAN_COMMENT_TASKS,
      ]
    },
    templateVariables () {
      return {
        '--access-rights-columns-count': this.displayedColumns.length,
      }
    },
  },
  created () {
    this.prepareGetRoomRights()
  },
  beforeUnmount () {
    this.RESET_ROOM_RIGHTS()
  },
  methods: {
    ...mapActions('room', [GET_ROOM_RIGHTS]),
    ...mapMutations('room', [RESET_ROOM_RIGHTS]),
    async prepareGetRoomRights () {
      try {
        await this.GET_ROOM_RIGHTS(this.mnemo)
      } catch (error) {
        if (error.response?.status === 404) {
          this.$router.replace({ name: ROOMS_ROUTE_NAME })
        }
        toast.error(this.$t('project.project-settings.access-rights.AccessRights.getRoomRightsError'))
      }
    },
    toggleGroup (groupId) {
      const groupIndex = this.openedGroups.findIndex(g => g === groupId)
      if (groupIndex >= 0) {
        this.openedGroups.splice(groupIndex, 1)
      } else {
        this.openedGroups.push(groupId)
      }
    },
    patchRoomRightsSuccess () {
      toast.success(this.$t('project.project-settings.access-rights.AccessRights.patchRoomRightsSuccess'))
    },
    patchRoomRightsError () {
      toast.error(this.$t('project.project-settings.access-rights.AccessRights.patchRoomRightsError'))
    },
  },
}
</script>

<style scoped lang="scss">
:deep(*) {
  .AccessRightsTable {
    &-grid {
      overflow-x: auto;
    }
    &-gridCell,
    &-gridHeaderCell,
    &-memberGridCell {
      padding: 10px;
      justify-self: stretch;
      background-color: rgb(var(--v-theme-surface-primary));
      border-right: 1px solid rgb(var(--v-theme-border-primary));
    }
    &-gridHeaderCell {
      border-bottom: 1px solid rgb(var(--v-theme-border-primary));
    }
    &-gridCell {
      background-color: rgb(var(--v-theme-surface-tertiary));
    }
    &-groupChevron {
      transition: transform .5s;
      &--open {
        transform: rotate(90deg);
      }
    }
    &-checkbox .v-selection-control {
      justify-content: center;
    }
  }
}
:deep(.sticky-left) {
  position: sticky;
  left: 0;
  z-index: 1;
}
</style>
